import React from "react";
import { Grid2, useTheme } from "@mui/material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import CustomTextField from "../../../../../../views/components/common/textFields/TextField";
import InlineFormError from "./InlineFormError";

export type QcProductQtyField = "tested" | "rejected";

interface QcProductQtyInputsProps {
  tested: number | null | undefined;
  rejected: number | null | undefined;
  /** Tested cap: unchecked qty, or qty still to receive when counting at QC. */
  checkableQty: number;
  /** Hint label for checkableQty (default "Unchecked"). */
  checkableLabel?: string;
  /** Reject-only entry (legacy rows that can only be confirmed). */
  hideTested?: boolean;
  onChange: (field: QcProductQtyField, value: number | null) => void;
  disabled?: boolean;
  errorMessage?: string;
}

const formatQtyInputValue = (quantity: number | null | undefined) =>
  quantity === null || quantity === undefined ? "" : quantity;

/** Product-level (no SKU variants) Tested + Rejected QC inputs. */
const QcProductQtyInputs: React.FC<QcProductQtyInputsProps> = ({
  tested,
  rejected,
  checkableQty,
  checkableLabel = "Unchecked",
  hideTested = false,
  onChange,
  disabled,
  errorMessage,
}) => {
  const theme = useTheme();

  const fields: Array<{
    key: QcProductQtyField;
    label: string;
    value: number | null | undefined;
  }> = [
    ...(hideTested
      ? []
      : [{ key: "tested" as const, label: "Tested", value: tested }]),
    { key: "rejected", label: "Rejected", value: rejected },
  ];
  const testedQty = tested ?? 0;

  return (
    <>
      <Grid2 container columns={12} spacing={2}>
        {fields.map((field) => (
          <Grid2 key={field.key} size={{ xs: 12, sm: 6 }}>
            <CustomTypography
              variant="heading8"
              color={theme.palette.customColors.greyTextColor}
              style={{ marginBottom: 4, display: "block" }}
            >
              {field.label}
            </CustomTypography>
            <CustomTextField
              type="number"
              allowDecimalValue={false}
              allowNegativeValue={false}
              borderColor={`0.1px solid ${
                errorMessage
                  ? theme.palette.error.main
                  : theme.palette.customColors.greyDarkborder
              }`}
              error={Boolean(errorMessage)}
              value={formatQtyInputValue(field.value)}
              placeholder="00"
              width="100%"
              onChange={(e) =>
                onChange(
                  field.key,
                  e.target.value === "" ? null : Number(e.target.value),
                )
              }
              disabled={disabled}
            />
          </Grid2>
        ))}
      </Grid2>
      <CustomTypography
        variant="caption"
        color={theme.palette.customColors.greyTextColor}
        style={{ marginTop: 6, display: "block" }}
      >
        {checkableLabel}: {checkableQty}
        {!hideTested && testedQty > 0
          ? ` · Passing: ${Math.max(0, testedQty - (rejected ?? 0))}`
          : ""}
      </CustomTypography>
      <InlineFormError message={errorMessage} />
    </>
  );
};

export default QcProductQtyInputs;
